import React, {
  useState, Dispatch, useEffect, useRef,
} from "react"
import { useHistory, useParams } from "react-router-dom"
import Spinner from "components/elements/Spinner"
import Modal from "components/dialogs/Modal"
import { Flex, HStack, Text } from "@chakra-ui/react"
import FullScreenModalNav from "components/dialogs/FullScreenModalNav"
import Link from "components/elements/Link"
import Icon from "components/elements/Icon"
import { useForm } from "queries/forms"
import {
  BuilderBlockWithBlockId, DioboxEvent, Form,
} from "sharedTypes"
import { patchForm } from "api/forms"
import { updateForm } from "context/actions"
import { useQueryClient } from "react-query"
import { stripOutQuestionsFormBlockData } from "components/Builder/helpers"
import { useDeepCompareEffect } from "ahooks"
import { isEqual } from "lodash"
import { DraggableItem } from "sharedTypes/builder"
import { BlockInsertPosition } from "components/Builder/BuildingBlock"
import { eventFormPath } from "utilities/routes"
import EditFormModalContent from "./EditFormModalContent/EditFormModalContent"

const UNSAVED_CHANGES_MESSAGE = "Changes you made may not be saved. Are you sure you want to leave?"

// Panels seed currentForm with "" for null and extra keys: compare saved keys only, "" as null
const normalize = (value: unknown) => (value === "" ? null : value)

const formHasChanges = (saved: Form, current: Form, blocks: BuilderBlockWithBlockId[]) => (
  (Object.keys(saved) as (keyof Form)[])
    .filter((key) => key !== "formData" && key !== "spotsLeft")
    .some((key) => !isEqual(normalize(current[key]), normalize(saved[key])))
  || !isEqual(blocks.map(({ block }) => block), saved.formData.blocks)
)

type Props = {
  blocks: BuilderBlockWithBlockId[];
  onBlockContentChange: (data: {}, selectedBlockId?: number) => void
  onDeleteBlock: () => void
  setSelectedBlockId: React.Dispatch<React.SetStateAction<number | undefined>>
  setBlocks: Dispatch<React.SetStateAction<BuilderBlockWithBlockId[]>>
  onDuplicateBlock: (blockId: number) => void
  selectedBlockId?: number
  event: DioboxEvent
  onDropBlock: (item: DraggableItem, blockId: number, position: BlockInsertPosition) => void
}
const EditForm = ({
  blocks,
  onBlockContentChange,
  setSelectedBlockId,
  selectedBlockId,
  setBlocks,
  onDeleteBlock,
  onDuplicateBlock,
  event,
  onDropBlock,
}: Props) => {
  const { eventId, formId } = useParams<{eventId: string, formId: string}>()
  const history = useHistory()
  const { isLoading, data: form } = useForm(eventId, formId)
  const [currentForm, setCurrentForm] = useState(form)
  const queryClient = useQueryClient()
  const unblockRef = useRef<(() => void) | null>(null)

  const hasChanges = Boolean(form && currentForm && formHasChanges(form, currentForm, blocks))

  useDeepCompareEffect(() => {
    if (!form) {
      return
    }

    setCurrentForm((prev) => {
      if (!prev) {
        return form
      }

      return {
        ...prev,
        translations: form.translations,
        languageTemplateId: form.languageTemplateId,
      }
    })
  }, [form])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasChanges) {
        return undefined
      }

      e.preventDefault()
      e.returnValue = UNSAVED_CHANGES_MESSAGE

      return UNSAVED_CHANGES_MESSAGE
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [hasChanges])

  useEffect(() => {
    unblockRef.current = history.block(() => (hasChanges ? UNSAVED_CHANGES_MESSAGE : undefined))

    return () => {
      unblockRef.current?.()
      unblockRef.current = null
    }
  }, [history, hasChanges])

  if (isLoading) {
    return <Spinner heightFull />
  }

  if (!currentForm) {
    return null
  }

  const handleUpdateAttributes = (formData: Form) => {
    setCurrentForm(formData)
  }

  const closeEditor = () => history.push(eventFormPath(eventId, formId))

  const handleSaveForm = () => {
    const formWithoutQuestionsFormBlockData = stripOutQuestionsFormBlockData({
      ...currentForm,
      formData: { blocks: blocks.map(({ block }) => block) },
    })

    patchForm(eventId, currentForm.externalId, formWithoutQuestionsFormBlockData)
      .then(({ data: updatedForm }) => {
        updateForm(queryClient, currentForm.externalId, updatedForm)
        unblockRef.current?.()
        unblockRef.current = null
        closeEditor()
      })
  }

  return (
    <Modal
      show
      size="full"
      hide={() => {}}
      header={(
        <FullScreenModalNav type="header">
          <Text w={1 / 3}>{`Customize Form${hasChanges ? "*" : ""}`}</Text>
          <HStack spacing={4} w={1 / 3} justify="flex-end">
            <Link onClick={closeEditor} color="gray.500" fontWeight="bold">
              Cancel
            </Link>
            <Link onClick={handleSaveForm} color="blue.300" fontWeight="bold">
              Save and Close
              <Icon icon="cancel-circle" ml={2} />
            </Link>
          </HStack>
        </FullScreenModalNav>
      )}
      noPadding
    >
      <Flex direction="column" h="full">
        <EditFormModalContent
          form={currentForm}
          setForm={setCurrentForm}
          blocks={blocks}
          onBlockContentChange={onBlockContentChange}
          onDeleteBlock={onDeleteBlock}
          setSelectedBlockId={setSelectedBlockId}
          selectedBlockId={selectedBlockId}
          setBlocks={setBlocks}
          onDuplicateBlock={onDuplicateBlock}
          onAttributeChange={handleUpdateAttributes}
          event={event}
          onDropBlock={onDropBlock}
        />
      </Flex>
    </Modal>
  )
}

export default EditForm
